import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, CheckCircle2, Clock, AlertTriangle, Loader2, X } from 'lucide-react';

const today = () => new Date().toISOString().split('T')[0];
const money = (n) => {
  const v = Number(n) || 0;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
// App-wide date rule: MM/DD/YYYY.
const fmtMDY = (val) => {
  if (!val) return '';
  const d = new Date(String(val).length <= 10 ? `${val}T00:00:00` : val);
  if (isNaN(d)) return String(val);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

/**
 * Big, center-screen modal that nudges the owner to confirm a draw's deposit.
 * Requires the deposit date so interest accrues accurately. Reused by the
 * on-login manager and the public (no-login) confirm page. When many draws are
 * pending, an optional "dismiss for now" escape hatch (onDismiss) is shown.
 */
export default function DrawConfirmationModal({ draw, index = 0, total = 1, onConfirm, onSnooze, onDiscard, onDismiss }) {
  const [depositDate, setDepositDate] = useState(today());
  const [busy, setBusy] = useState(null); // 'confirm' | 'snooze' | 'discard'
  const items = draw.items || [];
  // Per-item paid state. Default every item to PAID on the deposit date (the
  // common case); the owner flips the few that aren't paid yet to "Not yet".
  // `touched` tracks a manually-edited date so it doesn't get overwritten when
  // the deposit date changes.
  const [itemState, setItemState] = useState(() =>
    Object.fromEntries(items.map((it) => [it.phase_id, { paid: true, date: today(), touched: false }]))
  );

  useEffect(() => {
    setItemState((prev) => {
      const next = { ...prev };
      for (const it of items) {
        if (!next[it.phase_id]) next[it.phase_id] = { paid: true, date: depositDate, touched: false };
        else if (!next[it.phase_id].touched) next[it.phase_id] = { ...next[it.phase_id], date: depositDate };
      }
      return next;
    });
  }, [depositDate]);

  const setItem = (pid, patch) =>
    setItemState((prev) => ({ ...prev, [pid]: { ...(prev[pid] || { paid: true, date: depositDate }), ...patch } }));

  const buildItemPayments = () =>
    items.map((it) => {
      const st = itemState[it.phase_id] || { paid: true, date: depositDate };
      return { phase_id: it.phase_id, paid: !!st.paid, paid_date: st.paid ? (st.date || depositDate) : null };
    });

  const run = async (action, fn, arg) => {
    setBusy(action);
    try { await fn(arg); } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/75" data-testid="draw-confirmation-modal">
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-blue-200 p-6">
        {onDismiss && (
          <button
            onClick={onDismiss}
            data-testid="draw-dismiss-btn"
            aria-label="Dismiss for now"
            className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {total > 1 && (
          <p className="text-[11px] text-slate-400 text-center mb-1">{total} draws awaiting confirmation</p>
        )}
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-3">
            <DollarSign className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">Did your draw get deposited?</h2>
          <p className="text-sm text-slate-500 mt-1">
            {money(draw.amount)} draw{draw.draw_number ? ` (#${draw.draw_number})` : ''} for <b>{draw.project_address || draw.project_name}</b>.
          </p>
        </div>

        <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
            <Calendar className="w-4 h-4 text-blue-600" /> When did the funds hit your account?
          </label>
          <p className="text-[11px] text-slate-500 mb-2">Required — interest starts accruing from this exact date, so your balance stays accurate.</p>
          <input
            type="date"
            value={depositDate}
            onChange={(e) => setDepositDate(e.target.value)}
            data-testid="draw-confirm-deposit-date"
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-800 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
          />
          {depositDate && (
            <p className="text-[11px] text-slate-500 mt-1.5">Interest will start <b className="text-slate-700">{fmtMDY(depositDate)}</b></p>
          )}
        </div>

        {items.length > 0 && (
          <div className="mt-4" data-testid="draw-confirm-items">
            <p className="text-xs font-semibold text-slate-700">Which items did you pay?</p>
            <p className="text-[11px] text-slate-500 mb-2">Defaults to paid on the deposit date — switch any you haven't paid yet to "Not yet". You can set each item's real paid date.</p>
            <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
              {items.map((it) => {
                const st = itemState[it.phase_id] || { paid: true, date: depositDate };
                return (
                  <div key={it.phase_id} className="rounded-xl border border-slate-200 p-3" data-testid={`draw-confirm-item-${it.phase_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{it.name}</p>
                        <p className="text-[11px] text-slate-500">{money(it.amount)}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => setItem(it.phase_id, { paid: true })}
                          data-testid={`item-paid-yes-${it.phase_id}`}
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${st.paid ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                        >
                          Paid
                        </button>
                        <button
                          type="button"
                          onClick={() => setItem(it.phase_id, { paid: false })}
                          data-testid={`item-paid-no-${it.phase_id}`}
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${!st.paid ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                        >
                          Not yet
                        </button>
                      </div>
                    </div>
                    {st.paid && (
                      <div className="mt-2 flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <input
                          type="date"
                          value={st.date}
                          onChange={(e) => setItem(it.phase_id, { date: e.target.value, touched: true })}
                          data-testid={`item-paid-date-${it.phase_id}`}
                          className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-5 space-y-2">
          <button
            onClick={() => run('confirm', onConfirm, { depositDate, itemPayments: buildItemPayments() })}
            disabled={!!busy || !depositDate}
            data-testid="draw-confirm-btn"
            className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3 flex items-center justify-center gap-2 transition-colors"
          >
            {busy === 'confirm' ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
            Yes — record this draw
          </button>
          <button
            onClick={() => run('snooze', onSnooze)}
            disabled={!!busy}
            data-testid="draw-snooze-btn"
            className="w-full rounded-xl border border-slate-300 text-slate-700 py-2.5 text-sm hover:bg-slate-50 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          >
            {busy === 'snooze' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
            Not yet — remind me later
          </button>
          <button
            onClick={() => run('discard', onDiscard)}
            disabled={!!busy}
            data-testid="draw-discard-btn"
            className="w-full text-red-500 hover:text-red-700 py-1.5 text-xs flex items-center justify-center gap-1 disabled:opacity-60"
          >
            {busy === 'discard' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            It fell through — discard this draw
          </button>
          {onDismiss && (
            <button
              onClick={onDismiss}
              data-testid="draw-dismiss-link"
              className="w-full text-slate-400 hover:text-slate-600 py-1 text-[11px]"
            >
              Dismiss for now — I'll handle these later
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

